import React from 'react';
import { isOverFridgeCabinet } from '@shared-lib/kitchen-assembly/overFridgeCap';
import { ApplianceShell } from './ApplianceFrame';
import {
  cabinetFrontLayout,
  resolveDoorStyle,
  resolveHardwareType,
} from '../../3d/geometry/proceduralRenderers/cabinetFrontLayout';
import { rollOutCountOf } from '@shared-lib/cabinet-runs/rollOuts';
import { cabinetRollOutSlots } from '@shared-lib/cabinet-runs/cabinetInterior';

/**
 * Shared glue for the box-cabinet elevation schematics (`lower-cabinet`,
 * `upper-cabinet`).
 *
 * The schematic reads the SAME object fields `ProceduralObject.jsx` hands
 * the 3D `cabinetRenderer` as `options` —
 *   - `obj.columns: [{ width, type, drawerCount?, apronDrop? }]`
 *   - `obj.remainderAlign: 'left' | 'right' | 'center'`
 *   - `obj.doorStyle`: a drawn style (`@shared-lib/cabinet-runs/doorStyles`)
 *   - `obj.hardwareType: 'bar-pull' | 'knob' | 'cup-pull' | 'handleless'`
 *   - `obj.dimensions.handleAtBottom` (over-fridge uppers)
 *   - `obj.handleFinish` / `obj.paintColor` (not visualized — a line
 *     schematic has no colour)
 * — and runs the SAME `cabinetFrontLayout`, so every door, drawer, rail and
 * pull lands where the mesh puts it.
 */
export function resolveCabinetSchematic(
  obj = {},
  { width, height, depth },
  isUpperCabinet
) {
  const layout = cabinetFrontLayout(
    { width, height, depth, handleAtBottom: obj.dimensions?.handleAtBottom },
    {
      isUpperCabinet,
      isOverFridge: isOverFridgeCabinet(obj),
      columns: obj.columns,
      remainderAlign: obj.remainderAlign,
      endShelfSide: obj.endShelfSide,
      angledEndSide: obj.angledEndSide,
      underCabinetLight: obj.underCabinetLight === true,
      glassHolder: obj.glassHolder === true,
    }
  );
  return {
    layout,
    doorStyle: resolveDoorStyle(obj.doorStyle),
    hardwareType: resolveHardwareType(obj.hardwareType),
    // the roll-out trays behind a base's doors, at the slots the 3D view
    // draws and the estimate bills (none on a box that takes none)
    rollOutSlots: cabinetRollOutSlots(layout, rollOutCountOf(obj)),
  };
}

/**
 * Cabinet outer shell — same outer rect as ApplianceShell. Re-exported
 * so cabinet schematics don't need a second import line.
 */
export function CabinetShell(props) {
  return <ApplianceShell {...props} />;
}
